import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, GraduationCap, BookOpen, Hash, Edit, Check, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../../shared/contexts/AuthContext';
import { db } from '../../shared/api/firebase';
import { saveShortFormQuiz, generateShortFormQuizAI } from './ShortFormQuizService';
import '../../styles/contents/shortFormQuiz/ShortFormQuizSettingPage.css';

// 게임 컴포넌트들을 직접 import 합니다.
import ShortFormQuizBalloonPop from './ShortFormQuizBalloonPop';
import ShortFormQuizRobotFactory from './ShortFormQuizRobotFactory';
import ShortFormQuizBubbleWrap from './ShortFormQuizBubbleWrap';
import ShortFormQuizEraseAndPick from './ShortFormQuizEraseAndPick';

const GAME_TYPES = [
  { id: 'BubbleWrap', name: '뽁뽁이 터트리기', component: ShortFormQuizBubbleWrap, enabled: true },
  { id: 'BalloonPop', name: '풍선 터트리기', component: ShortFormQuizBalloonPop, enabled: true },
  { id: 'RobotFactory', name: '로봇 공장', component: ShortFormQuizRobotFactory, enabled: false },
  { id: 'EraseAndPick', name: '지우고 고르기', component: ShortFormQuizEraseAndPick, enabled: true },
];

// 과목 목록 정의 (학교급별 및 학년별)
const SUBJECTS_BY_SCHOOL = {
  elementary: {
    1: ['국어', '수학', '통합', '창의적 체험활동'],
    2: ['국어', '수학', '통합', '창의적 체험활동'],
    3: ['국어', '수학', '사회', '도덕', '과학', '체육', '음악', '미술', '영어', '창의적 체험활동'],
    4: ['국어', '수학', '사회', '도덕', '과학', '체육', '음악', '미술', '영어', '창의적 체험활동'],
    5: ['국어', '수학', '사회', '도덕', '과학', '체육', '음악', '미술', '영어', '창의적 체험활동'],
    6: ['국어', '수학', '사회', '도덕', '과학', '체육', '음악', '미술', '영어', '창의적 체험활동']
  },
  middle: [
    '국어', '수학', '사회', '과학', '영어', '음악', '미술', '체육', '기술·가정', '도덕', '한문', '정보'
  ],
  high: [
    '국어', '수학', '사회', '과학', '영어', '음악', '미술', '체육', '기술·가정', '제2외국어', '한문', '교양'
  ]
};

// 과목별 주제 목록 정의
const TOPICS_BY_SUBJECT = {
  국어: ['문학', '화법과 작문', '독서', '언어와 매체', '고전', '시', '소설', '수필', '희곡', '문법'],
  수학: ['수와 연산', '도형', '측정', '규칙성', '자료와 가능성', '함수', '기하', '확률과 통계', '미적분'],
  과학: ['물리', '화학', '생물', '지구과학', '환경', '우주', '동물', '식물', '물질의 성질', '에너지'],
  사회: ['지리', '역사', '일반사회', '경제', '정치', '문화', '세계사', '한국사', '인권', '민주주의'],
  영어: ['듣기', '말하기', '읽기', '쓰기', '문법', '어휘', '의사소통', '문화', '일상대화', '여행영어'],
  음악: ['서양음악', '국악', '대중음악', '음악이론', '작곡가', '악기', '성악', '합창', '리듬', '화성'],
  미술: ['회화', '조소', '디자인', '공예', '서예', '미술사', '색채', '구도', '표현기법', '감상'],
  체육: ['체조', '육상', '구기', '수영', '무용', '건강', '안전', '스포츠', '운동기능', '체력'],
  '기술·가정': ['가정생활', '기술', '정보통신', '진로', '창업', '발명', '제조기술', '생활기술'],
  도덕: ['자아존중', '타인배려', '정의', '책임', '인권', '평화', '환경윤리', '생명윤리', '사회정의'],
  한문: ['한자', '한문', '고사성어', '명언', '고전문학', '사자성어', '문장구조', '어법'],
  정보: ['컴퓨팅 사고력', '자료와 정보', '추상화', '알고리즘', '프로그래밍', 'AI', '데이터과학'],
  '창의적 체험활동': ['자율활동', '동아리활동', '봉사활동', '진로활동', '리더십', '협동', '창의성'],
  통합: ['학교', '사람들', '우리나라', '이웃', '자연', '계절', '가족', '안전', '마을', '생활', '계절']
};

// 학년별 과목 목록을 가져오는 헬퍼 함수
const getSubjectsForGrade = (schoolType, grade) => {
  if (schoolType === 'elementary') {
    return SUBJECTS_BY_SCHOOL.elementary[grade] || [];
  }
  return SUBJECTS_BY_SCHOOL[schoolType] || [];
};

const ShortFormQuizSettingPage = () => {
  const navigate = useNavigate();

  // step: 1 = 생성 방식 선택, 2 = 학교/학년/과목 입력, 4 = 로딩, 5 = 미리보기+저장확인, 6 = 제목 저장
  const [step, setStep] = useState(1);
  const [generationMode, setGenerationMode] = useState(null); // 'ai' | 'manual'
  const [grade, setGrade] = useState(null); // 숫자 (초 1~6, 중/고 1~3)
  const [schoolType, setSchoolType] = useState('elementary'); // 'elementary' | 'middle' | 'high' | 'custom'
  const [customSchoolName, setCustomSchoolName] = useState('');
  const [subjectInput, setSubjectInput] = useState('');
  const [topicInput, setTopicInput] = useState('');
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [subjectMode, setSubjectMode] = useState('input'); // 'select' | 'input'
  const [topicMode, setTopicMode] = useState('input'); // 'select' | 'input'
  const [selectedPredefinedSubject, setSelectedPredefinedSubject] = useState('');
  const [selectedPredefinedTopic, setSelectedPredefinedTopic] = useState('');
  const [fakeQuiz, setFakeQuiz] = useState(null);
  const [title, setTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [validationErrors, setValidationErrors] = useState(new Set());
  const [isGeneratingMore, setIsGeneratingMore] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { currentUser } = useAuth();

  // quizType 상태 추가 및 초기화
  const [quizType, setQuizType] = useState(GAME_TYPES.find(g => g.enabled)?.id || '');

  // 모바일 감지
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  // 수동 생성용 빈 퀴즈 템플릿
  const generateEmptyQuizData = (numQuestions = 1) => {
    return {
      meta: { mode: 'manual', grade: null, subject: null, topic: '' },
      questions: Array.from({ length: numQuestions }).map(() => ({
        question: '',
        options: ['', '', '', ''],
        answerIndex: 0
      }))
    };
  };
  // 과목 선택 → 로딩 → 가짜 퀴즈 생성 → 미리보기
  const handleSubjectSelection = async (subject) => {
    setSelectedSubject(subject);
    setStep(4); // 로딩 단계
    setFakeQuiz(null);

    if (generationMode === 'ai') {
      try {
        const ai = await generateShortFormQuizAI({
          school: schoolType === 'custom' ? (customSchoolName || '') : (schoolType === 'elementary' ? '초등학교' : schoolType === 'middle' ? '중학교' : '고등학교'),
          grade,
          subject,
          topic: topicInput?.trim() || '',
          numQuestions: 5,
          curriculum: '대한민국 국가교육과정', // 대한민국 교육과정 조건 추가
          educationStandard: '2022 개정 교육과정' // 최신 교육과정 기준 명시
        });
        const quiz = {
          meta: { mode: 'ai', grade, subject, topic: topicInput?.trim() || '' },
          questions: ai?.questions || []
        };
        setFakeQuiz(quiz);
        setIsEditing(true); // AI 생성 후 바로 편집 모드로 설정
      } catch (e) {
        const quiz = generateFakeQuizData(grade, subject, generationMode);
        setFakeQuiz(quiz);
        setIsEditing(true); // 오류 시에도 편집 모드로 설정
      } finally {
        setStep(6);
      }
    } else {
      // 수동 모드: 기존 가짜 데이터
      const quiz = generateFakeQuizData(grade, subject, generationMode);
      setFakeQuiz(quiz);
      setIsEditing(true); // 수동 모드도 편집 모드로 설정
      setStep(6);
    }
  };

  // 편집 핸들러들
  const handleQuestionTextChange = (qIndex, value) => {
    setFakeQuiz((prev) => {
      if (!prev) return prev;
      const nextQuestions = [...(prev.questions || [])];
      const target = { ...(nextQuestions[qIndex] || {}) };
      target.question = value;
      nextQuestions[qIndex] = target;
      return { ...prev, questions: nextQuestions };
    });
  };

  const handleOptionTextChange = (qIndex, optIndex, value) => {
    setFakeQuiz((prev) => {
      if (!prev) return prev;
      const nextQuestions = [...(prev.questions || [])];
      const target = { ...(nextQuestions[qIndex] || {}) };
      const opts = Array.isArray(target.options) ? [...target.options] : [];
      // 4지선다 고정 보정
      while (opts.length < 4) opts.push(`선택지 ${String.fromCharCode(65 + opts.length)}`);
      opts[optIndex] = value;
      target.options = opts.slice(0, 4);
      nextQuestions[qIndex] = target;
      return { ...prev, questions: nextQuestions };
    });
  };

  const handleAnswerIndexChange = (qIndex, newIndex) => {
    setFakeQuiz((prev) => {
      if (!prev) return prev;
      const nextQuestions = [...(prev.questions || [])];
      const target = { ...(nextQuestions[qIndex] || {}) };
      target.answerIndex = newIndex;
      nextQuestions[qIndex] = target;
      return { ...prev, questions: nextQuestions };
    });
  };

  // 문제 추가
  const addQuestion = () => {
    setFakeQuiz((prev) => {
      if (!prev) return prev;
      const currentQuestions = prev.questions || [];
      if (currentQuestions.length >= 20) {
        alert('최대 20문제까지만 추가할 수 있습니다.');
        return prev;
      }
      const newQuestion = {
        question: '',
        options: ['', '', '', ''],
        answerIndex: 0
      };
      return {
        ...prev,
        questions: [...currentQuestions, newQuestion]
      };
    });
  };

  // AI로 추가 문제 생성
  const generateMoreQuestions = async () => {
    if (!fakeQuiz || isGeneratingMore) return;
    
    const currentQuestions = fakeQuiz.questions || [];
    const remainingSlots = 20 - currentQuestions.length;
    
    if (remainingSlots <= 0) {
      alert('최대 20문제까지만 추가할 수 있습니다.');
      return;
    }
    
    const questionsToGenerate = Math.min(5, remainingSlots);
    
    setIsGeneratingMore(true);
    
    try {
      const ai = await generateShortFormQuizAI({
        school: schoolType === 'custom' ? (customSchoolName || '') : (schoolType === 'elementary' ? '초등학교' : schoolType === 'middle' ? '중학교' : '고등학교'),
        grade,
        subject: selectedSubject,
        topic: topicInput?.trim() || '',
        numQuestions: questionsToGenerate,
        curriculum: '대한민국 국가교육과정', // 대한민국 교육과정 조건 추가
        educationStandard: '2022 개정 교육과정' // 최신 교육과정 기준 명시
      });
      
      if (ai?.questions && ai.questions.length > 0) {
        setFakeQuiz((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            questions: [...(prev.questions || []), ...ai.questions]
          };
        });
      } else {
        alert('AI 문제 생성에 실패했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      console.error('AI 문제 생성 오류:', error);
      alert('AI 문제 생성 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsGeneratingMore(false);
    }
  };

  // 문제 삭제
  const deleteQuestion = (qIndex) => {
    setFakeQuiz((prev) => {
      if (!prev) return prev;
      const currentQuestions = prev.questions || [];
      if (currentQuestions.length <= 1) {
        alert('최소 1문제는 있어야 합니다.');
        return prev;
      }
      const nextQuestions = currentQuestions.filter((_, idx) => idx !== qIndex);
      return { ...prev, questions: nextQuestions };
    });
  };

  // 빈칸 검증
  const validateQuestions = () => {
    if (!fakeQuiz || !fakeQuiz.questions) return true;
    
    const errors = new Set();
    fakeQuiz.questions.forEach((q, qIndex) => {
      // 문제 제목 검증
      if (!q.question?.trim()) {
        errors.add(`question_${qIndex}`);
      }
      // 선택지 검증
      q.options?.forEach((opt, optIndex) => {
        if (!opt?.trim()) {
          errors.add(`option_${qIndex}_${optIndex}`);
        }
      });
    });
    
    setValidationErrors(errors);
    return errors.size === 0;
  };

  // 가짜 퀴즈 데이터 생성기
  const generateFakeQuizData = (g, subject, mode) => {
    const baseQuestions = [
      {
        question: `${subject} 상식 퀴즈: 1번 문제`,
        options: ['보기 1', '보기 2', '보기 3', '보기 4'],
        answerIndex: 1
      },
      {
        question: `${subject} 핵심 개념: 2번 문제`,
        options: ['선택지 A', '선택지 B', '선택지 C', '선택지 D'],
        answerIndex: 0
      },
      {
        question: `${subject} 기초 확인: 3번 문제`,
        options: ['①', '②', '③', '④'],
        answerIndex: 2
      },
      {
        question: `${subject} 응용 문제: 4번 문제`,
        options: ['정답 1', '정답 2', '정답 3', '정답 4'],
        answerIndex: 3
      },
      {
        question: `${subject} 개념 점검: 5번 문제`,
        options: ['True', 'False', '모름', '부분 정답'],
        answerIndex: 0
      }
    ];

    return {
      meta: {
        mode: mode || 'manual',
        grade: g,
        subject
      },
      questions: baseQuestions
    };
  };

  const handleBackNavigation = () => {
    setStep(1); // Navigate to quiz creation mode selection
  };

  const handleHomeButtonClick = () => {
    // 작업 중인 내용이 있는지 확인
    const hasWorkInProgress = 
      generationMode || 
      fakeQuiz || 
      title.trim() || 
      (step > 1 && (grade || subjectInput.trim() || topicInput.trim()));
    
    if (hasWorkInProgress) {
      setShowExitModal(true);
    } else {
      navigate(-1);
    }
  };

  const confirmExit = () => {
    setShowExitModal(false);
    navigate(-1);
  };

  const cancelExit = () => {
    setShowExitModal(false);
  };

  const handleSaveWithTitle = () => {
    if (!title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    setIsSaving(true);
    const persist = async () => {
      try {
        // localStorage에서 classId 가져오기
        const getStoredClassData = () => {
          try {
            const classInfo = localStorage.getItem('classInfo');
            return classInfo ? JSON.parse(classInfo) : null;
          } catch (error) {
            return null;
          }
        };
        const storedClassData = getStoredClassData();
        
        const result = await saveShortFormQuiz({
          title: title.trim(),
          grade,
          subject: selectedSubject,
          school: schoolType === 'custom' ? (customSchoolName || '') : (schoolType === 'elementary' ? '초등학교' : schoolType === 'middle' ? '중학교' : '고등학교'),
          mode: generationMode,
          topic: topicInput?.trim() || '',
          questions: fakeQuiz?.questions || [],
          currentUser,
          classId: storedClassData?.classId || null
        });
        if (!result?.success) {
          throw new Error('저장에 실패했습니다.');
        }
        navigate('/play/shortFormQuiz');
      } catch (e) {
        console.error(e);
        alert('저장 중 오류가 발생했습니다. 다시 시도해주세요.');
      } finally {
        setIsSaving(false);
      }
    };
    persist();
  };

  const gradeOptions = useMemo(() => {
    if (schoolType === 'middle' || schoolType === 'high') return [1, 2, 3];
    return [1, 2, 3, 4, 5, 6];
  }, [schoolType]);

  const goNextFromMode = (mode) => {
    setGenerationMode(mode);
    setFakeQuiz(null); // Reset quiz information
    if (mode === 'manual') {
      // 수동 모드: 곧바로 제목/문항 편집 화면으로 이동
      setFakeQuiz(generateEmptyQuizData(1));
      setIsEditing(true);
      setStep(6);
    } else {
      // AI 모드: 정보 입력 단계로 이동
      setStep(2);
    }
  };

  const handleSelectGrade = (selected) => {
    setGrade(selected);
    setFakeQuiz(null);
  };

  const getSchoolDisplayName = () => {
    if (schoolType === 'elementary') return '초등학교';
    if (schoolType === 'middle') return '중학교';
    if (schoolType === 'high') return '고등학교';
    return customSchoolName || '학교';
  };

  // 메타 텍스트(값이 있을 때만 노출)
  const getMetaText = () => {
    const parts = [];
    const schoolName = schoolType === 'custom' ? (customSchoolName || '') : getSchoolDisplayName();
    if (schoolName) parts.push(schoolName);
    if (grade) parts.push(`${grade}학년`);
    if (selectedSubject) parts.push(selectedSubject);
    if (topicInput) parts.push(`주제: ${topicInput}`);
    parts.push(generationMode === 'ai' ? 'AI 생성' : '직접 생성');
    return parts.join(' · ');
  };

  const canProceed = useMemo(() => {
    const hasSchool = schoolType === 'custom' ? !!customSchoolName.trim() : !!schoolType;
    return hasSchool && !!grade && !!subjectInput.trim() && !!topicInput.trim();
  }, [schoolType, customSchoolName, grade, subjectInput, topicInput]);

  return (
    <section className="content-start-section" style={{ 
      paddingTop: 'var(--spacing-lg)', 
      paddingBottom: 'var(--spacing-2xl)',
      minHeight: '100vh'
    }}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
        
        /* 모바일 최적화 스타일 */
        @media (max-width: 768px) {
          .mobile-container {
            padding: 0 12px !important;
          }
          .mobile-card {
            padding: 16px !important;
          }
          .mobile-button-container {
            flex-direction: column !important;
            gap: 12px !important;
          }
          .mobile-button {
            min-width: 100% !important;
            min-height: 200px !important;
          }
          .mobile-form-card {
            padding: 20px !important;
          }
          .mobile-segmented-control {
            width: 100% !important;
            flex-direction: column !important;
            gap: 4px !important;
            padding: 8px !important;
            border-radius: 12px !important;
          }
          .mobile-segmented-button {
            width: 100% !important;
            min-height: 44px !important;
            border-radius: 8px !important;
            margin: 0 !important;
          }
          .mobile-grid-2 {
            grid-template-columns: 1fr !important;
          }
          .mobile-modal {
            width: 95% !important;
            margin: 20px !important;
          }
          .mobile-title {
            font-size: 20px !important;
          }
          .mobile-section-title {
            font-size: 24px !important;
          }
          .mobile-input {
            padding: 16px 20px 16px 48px !important;
            font-size: 16px !important;
          }
          .mobile-button-large {
            padding: 16px 24px !important;
            font-size: 16px !important;
            min-height: 48px !important;
          }
          .mobile-chips-container {
            gap: 12px !important;
          }
          .mobile-chip {
            min-height: 48px !important;
            padding: 12px 16px !important;
            font-size: 16px !important;
          }
        }
        
        @media (max-width: 480px) {
          .mobile-title {
            font-size: 18px !important;
          }
          .mobile-section-title {
            font-size: 20px !important;
          }
          .mobile-button {
            min-height: 180px !important;
          }
        }
      `}</style>
      <div className="container mobile-container" style={{ maxWidth: '1200px', width: '100%', margin: '0 auto', padding: '0 20px' }}>
        <div className="content-navigation" style={{ marginBottom: 'var(--spacing-md)' }}>
          <button
            className="btn-ghost"
            onClick={handleHomeButtonClick}
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', border: '1px solid var(--gray-300)' }}
          >
            <ArrowLeft size={16} />
            홈으로
          </button>
        </div>

        <div className="tools-frame-content mobile-card" style={{ 
          maxWidth: '1200px',
          width: '100%',
          margin: '0 auto',
          padding: '20px 24px 60px'
        }}>
          {step === 1 && (
            <div style={{ display: 'grid', gap: 'var(--spacing-xl)', minHeight: '600px', paddingTop: '40px', paddingBottom: '40px' }}>
              <h2 className="section-title mobile-section-title" style={{ margin: 0, textAlign: 'center' }}>콘텐츠 생성 방식을 선택하세요</h2>
              <div className="mobile-button-container" style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                gap: 'var(--spacing-xl)',
                flex: 1,
                minHeight: '400px'
              }}>
                <button
                  onClick={() => goNextFromMode('ai')}
                  className="mobile-button"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px',
                    minHeight: 220,
                    minWidth: 320,
                    border: '1px solid var(--gray-300)',
                    borderRadius: '20px',
                    background: '#ffffff',
                    color: 'var(--gray-900)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 14px rgba(0,0,0,0.10)';
                    e.currentTarget.style.background = 'linear-gradient(90deg, #0066FF 0%, #4DB9FF 100%)';
                    e.currentTarget.style.color = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.06)';
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.color = 'var(--gray-900)';
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/images/contents/shortForm/AIQuizGenerationCharacter.png`}
                    alt="AI로 자동 생성"
                    style={{ width: 200, height: 200, objectFit: 'contain' }}
                    loading="lazy"
                  />
                  <span style={{ fontWeight: 700, fontSize: 20 }}>AI로 자동 생성</span>
                </button>

                <button
                  onClick={() => goNextFromMode('manual')}
                  className="mobile-button"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px',
                    minHeight: 220,
                    minWidth: 320,
                    border: '1px solid var(--gray-300)',
                    borderRadius: '20px',
                    background: '#ffffff',
                    color: 'var(--gray-900)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 14px rgba(0,0,0,0.10)';
                    e.currentTarget.style.background = 'linear-gradient(90deg, #0066FF 0%, #4DB9FF 100%)';
                    e.currentTarget.style.color = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.06)';
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.color = 'var(--gray-900)';
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/images/contents/shortForm/QuizProductionCharacter.png`}
                    alt="직접 문제 만들기"
                    style={{ width: 200, height: 200, objectFit: 'contain' }}
                    loading="lazy"
                  />
                  <span style={{ fontWeight: 700, fontSize: 20 }}>직접 문제 만들기</span>
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div style={{ display: 'grid', gap: 'var(--spacing-lg)' }}>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 44 }}>
                <button className="btn-secondary" onClick={handleBackNavigation}>이전</button>
                <button
                  className="btn-primary"
                  onClick={() => {
                    if (fakeQuiz) {
                      setStep(6);
                    } else {
                      handleSubjectSelection(subjectInput.trim());
                    }
                  }}
                  disabled={!canProceed}
                >
                  다음
                </button>
              </div>
              <div style={{ textAlign: 'center' }}>
                <h2 className="section-title mobile-section-title" style={{ margin: 0 }}>퀴즈에 대한 정보를 입력하세요</h2>
              </div>
              <div className="mobile-form-card" style={{
                background: '#fff',
                border: '1px solid var(--gray-200)',
                borderRadius: '16px',
                padding: 'var(--spacing-xl)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                display: 'grid',
                gap: 'var(--spacing-xl)',
                minHeight: '600px'
              }}>
                {/* 학교 선택 - Segmented Control */}
                <div style={{ display: 'grid', gap: 'var(--spacing-sm)' }}>
                  <label style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <GraduationCap size={18} /> 학교
                  </label>
                  <div className="mobile-segmented-control" style={{
                    display: 'flex',
                    gap: '8px',
                    background: 'var(--gray-50)',
                    border: '1px solid var(--gray-200)',
                    borderRadius: isMobile ? '12px' : '9999px',
                    padding: '6px',
                    width: isMobile ? '100%' : '584px',
                    justifyContent: 'space-between',
                    flexDirection: isMobile ? 'column' : 'row'
                  }}>
                    {[
                      { key: 'elementary', label: '초등학교' },
                      { key: 'middle', label: '중학교' },
                      { key: 'high', label: '고등학교' },
                      { key: 'custom', label: '직접 입력' }
                    ].map((opt) => {
                      const isActive = schoolType === opt.key;
                      return (
                        <button
                          key={opt.key}
                          className="mobile-segmented-button"
                          onClick={() => { 
                            setSchoolType(opt.key); 
                            setSelectedPredefinedSubject('');
                            setSelectedPredefinedTopic('');
                            setSubjectInput('');
                            setTopicInput('');
                            setFakeQuiz(null); 
                          }}
                          style={{
                            width: isMobile ? '100%' : '140px',
                            minHeight: isMobile ? 44 : 40,
                            padding: isMobile ? '12px 16px' : '8px 14px',
                            border: 'none',
                            borderRadius: isMobile ? '8px' : '9999px',
                            fontWeight: 700,
                            background: isActive ? 'white' : 'transparent',
                            color: isActive ? 'var(--gray-900)' : 'var(--gray-600)',
                            boxShadow: isActive ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                            borderBottom: isActive ? '1px solid var(--gray-200)' : 'none',
                            transition: 'all 0.15s ease',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.background = 'rgba(255,255,255,0.8)';
                              e.currentTarget.style.color = 'var(--gray-800)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.color = 'var(--gray-600)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {schoolType === 'custom' && (
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        className="mobile-input"
                        value={customSchoolName}
                        onChange={(e) => { setCustomSchoolName(e.target.value); setFakeQuiz(null); }}
                        placeholder="학교명을 입력하세요"
                        style={{
                          width: '100%',
                          border: '1px solid var(--gray-300)',
                          borderRadius: '12px',
                          padding: '12px 14px 12px 40px'
                        }}
                      />
                      <GraduationCap size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
                    </div>
                  )}
                </div>
                {/* 학년 선택 - Chips */}
                <div style={{ display: 'grid', gap: 'var(--spacing-sm)' }}>
                  <label style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Hash size={18} /> 학년
                  </label>
                  <div className="mobile-chips-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {gradeOptions.map((g) => {
                      const isActive = grade === g;
                      return (
                        <button
                          key={g}
                          className="mobile-chip"
                          onClick={() => handleSelectGrade(g)}
                          style={{
                            minWidth: 88,
                            minHeight: 44,
                            padding: '8px 14px',
                            borderRadius: '9999px',
                            border: isActive ? '1px solid var(--primary)' : '1px solid var(--gray-300)',
                            background: isActive ? 'var(--primary-light)' : 'white',
                            color: isActive ? 'var(--primary-dark, var(--primary))' : 'var(--gray-800)',
                            fontWeight: 700,
                            cursor: 'pointer',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.background = 'var(--gray-50)';
                              e.currentTarget.style.borderColor = 'var(--gray-400)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.background = 'white';
                              e.currentTarget.style.borderColor = 'var(--gray-300)';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }
                          }}
                        >
                          {g}학년
                        </button>
                      );
                    })}
                  </div>
                </div>
                {/* 과목 선택/입력 */}
                <div style={{ display: 'grid', gap: 'var(--spacing-sm)' }}>
                  <label style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <BookOpen size={18} /> 과목
                  </label>
                  
                  {/* 과목 모드 선택 */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    background: 'var(--gray-50)',
                    border: '1px solid var(--gray-200)',
                    borderRadius: '9999px',
                    padding: '4px'
                  }}>
                    <button
                      onClick={() => { setSubjectMode('input'); setFakeQuiz(null); }}
                      style={{
                        flex: 1,
                        minHeight: 32,
                        padding: '6px 12px',
                        border: 'none',
                        borderRadius: '9999px',
                        fontSize: 13,
                        fontWeight: 600,
                        background: subjectMode === 'input' ? 'white' : 'transparent',
                        color: subjectMode === 'input' ? 'var(--gray-900)' : 'var(--gray-600)',
                        boxShadow: subjectMode === 'input' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                        transition: 'all 0.15s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        if (subjectMode !== 'input') {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.6)';
                          e.currentTarget.style.color = 'var(--gray-800)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (subjectMode !== 'input') {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = 'var(--gray-600)';
                        }
                      }}
                    >
                      직접 입력
                    </button>
                    <button
                      onClick={() => { setSubjectMode('select'); setFakeQuiz(null); }}
                      style={{
                        flex: 1,
                        minHeight: 32,
                        padding: '6px 12px',
                        border: 'none',
                        borderRadius: '9999px',
                        fontSize: 13,
                        fontWeight: 600,
                        background: subjectMode === 'select' ? 'white' : 'transparent',
                        color: subjectMode === 'select' ? 'var(--gray-900)' : 'var(--gray-600)',
                        boxShadow: subjectMode === 'select' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                        transition: 'all 0.15s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        if (subjectMode !== 'select') {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.6)';
                          e.currentTarget.style.color = 'var(--gray-800)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (subjectMode !== 'select') {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = 'var(--gray-600)';
                        }
                      }}
                    >
                      목록에서 선택
                    </button>
                  </div>

                  {/* 과목 선택 모드 */}
                  {subjectMode === 'select' && (
                    <div>
                      {!grade ? (
                        <div style={{ 
                          padding: '12px 16px', 
                          background: 'var(--gray-50)', 
                          borderRadius: '8px', 
                          color: 'var(--gray-600)', 
                          fontSize: 14,
                          textAlign: 'center'
                        }}>
                          먼저 학년을 선택해주세요
                        </div>
                      ) : (
                        <div className="mobile-chips-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {getSubjectsForGrade(schoolType, grade).map((subject) => {
                        const isActive = selectedPredefinedSubject === subject;
                        return (
                          <button
                            key={subject}
                            className="mobile-chip"
                            onClick={() => { 
                              setSelectedPredefinedSubject(subject); 
                              setSubjectInput(subject);
                              setSelectedPredefinedTopic('');
                              setTopicInput('');
                              setFakeQuiz(null); 
                            }}
                            style={{
                              padding: '8px 12px',
                              borderRadius: '9999px',
                              border: isActive ? '1px solid var(--primary)' : '1px solid var(--gray-300)',
                              background: isActive ? 'var(--primary-light)' : 'white',
                              color: isActive ? 'var(--primary-dark, var(--primary))' : 'var(--gray-800)',
                              fontWeight: 600,
                              fontSize: 14,
                              cursor: 'pointer',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.background = 'var(--gray-50)';
                                e.currentTarget.style.borderColor = 'var(--gray-400)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.borderColor = 'var(--gray-300)';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }
                            }}
                          >
                            {subject}
                          </button>
                        );
                      })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* 과목 직접 입력 모드 */}
                  {subjectMode === 'input' && (
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        className="mobile-input"
                        value={subjectInput}
                        onChange={(e) => { 
                          setSubjectInput(e.target.value); 
                          setSelectedPredefinedSubject('');
                          setSelectedPredefinedTopic('');
                          setTopicInput('');
                          setFakeQuiz(null); 
                        }}
                        placeholder="예: 국어, 수학, 과학..."
                        style={{
                          width: '100%',
                          border: '1px solid var(--gray-300)',
                          borderRadius: '12px',
                          padding: '12px 14px 12px 40px'
                        }}
                      />
                      <BookOpen size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
                    </div>
                  )}
                </div>
                {/* 주제 선택/입력 */}
                <div style={{ display: 'grid', gap: 'var(--spacing-sm)' }}>
                  <label style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <BookOpen size={18} /> 주제
                  </label>
                  
                  {/* 주제 모드 선택 */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    background: 'var(--gray-50)',
                    border: '1px solid var(--gray-200)',
                    borderRadius: '9999px',
                    padding: '4px'
                  }}>
                    <button
                      onClick={() => { setTopicMode('input'); setFakeQuiz(null); }}
                      style={{
                        flex: 1,
                        minHeight: 32,
                        padding: '6px 12px',
                        border: 'none',
                        borderRadius: '9999px',
                        fontSize: 13,
                        fontWeight: 600,
                        background: topicMode === 'input' ? 'white' : 'transparent',
                        color: topicMode === 'input' ? 'var(--gray-900)' : 'var(--gray-600)',
                        boxShadow: topicMode === 'input' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                        transition: 'all 0.15s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        if (topicMode !== 'input') {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.6)';
                          e.currentTarget.style.color = 'var(--gray-800)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (topicMode !== 'input') {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = 'var(--gray-600)';
                        }
                      }}
                    >
                      직접 입력
                    </button>
                    <button
                      onClick={() => { setTopicMode('select'); setFakeQuiz(null); }}
                      style={{
                        flex: 1,
                        minHeight: 32,
                        padding: '6px 12px',
                        border: 'none',
                        borderRadius: '9999px',
                        fontSize: 13,
                        fontWeight: 600,
                        background: topicMode === 'select' ? 'white' : 'transparent',
                        color: topicMode === 'select' ? 'var(--gray-900)' : 'var(--gray-600)',
                        boxShadow: topicMode === 'select' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                        transition: 'all 0.15s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        if (topicMode !== 'select') {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.6)';
                          e.currentTarget.style.color = 'var(--gray-800)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (topicMode !== 'select') {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = 'var(--gray-600)';
                        }
                      }}
                    >
                      목록에서 선택
                    </button>
                  </div>

                  {/* 주제 선택 모드 */}
                  {topicMode === 'select' && (
                    <div>
                      {!selectedPredefinedSubject && !subjectInput ? (
                        <div style={{ 
                          padding: '12px 16px', 
                          background: 'var(--gray-50)', 
                          borderRadius: '8px', 
                          color: 'var(--gray-600)', 
                          fontSize: 14,
                          textAlign: 'center'
                        }}>
                          먼저 과목을 선택해주세요
                        </div>
                      ) : (
                        <div className="mobile-chips-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {(TOPICS_BY_SUBJECT[selectedPredefinedSubject || subjectInput] || []).map((topic) => {
                            const isActive = selectedPredefinedTopic === topic;
                            return (
                              <button
                                key={topic}
                                className="mobile-chip"
                                onClick={() => { 
                                  setSelectedPredefinedTopic(topic); 
                                  setTopicInput(topic);
                                  setFakeQuiz(null); 
                                }}
                                style={{
                                  padding: '8px 12px',
                                  borderRadius: '9999px',
                                  border: isActive ? '1px solid var(--primary)' : '1px solid var(--gray-300)',
                                  background: isActive ? 'var(--primary-light)' : 'white',
                                  color: isActive ? 'var(--primary-dark, var(--primary))' : 'var(--gray-800)',
                                  fontWeight: 600,
                                  fontSize: 14,
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease'
                                }}
                                onMouseEnter={(e) => {
                                  if (!isActive) {
                                    e.currentTarget.style.background = 'var(--gray-50)';
                                    e.currentTarget.style.borderColor = 'var(--gray-400)';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isActive) {
                                    e.currentTarget.style.background = 'white';
                                    e.currentTarget.style.borderColor = 'var(--gray-300)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }
                                }}
                              >
                                {topic}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* 주제 직접 입력 모드 */}
                  {topicMode === 'input' && (
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        className="mobile-input"
                        value={topicInput}
                        onChange={(e) => { 
                          setTopicInput(e.target.value); 
                          setSelectedPredefinedTopic('');
                          setFakeQuiz(null); 
                        }}
                        placeholder="예: 우주, 동물, 환경, 세계사 인물..."
                        style={{
                          width: '100%',
                          border: '1px solid var(--gray-300)',
                          borderRadius: '12px',
                          padding: '12px 14px 12px 40px'
                        }}
                      />
                      <BookOpen size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
                    </div>
                  )}
                </div>
              </div>
              {fakeQuiz && (
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 44 }}>
                  <button
                    className="btn-success"
                    onClick={() => handleSubjectSelection(subjectInput.trim())}
                    disabled={!canProceed}
                  >
                    퀴즈 재생성
                  </button>
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--spacing-md)',
              padding: 'var(--spacing-2xl)'
            }}>
              <Loader2 className="spin" size={32} />
              <h3 style={{ margin: 0 }}>{generationMode === 'ai' ? 'AI가 퀴즈를 생성중입니다...' : '퀴즈를 준비중입니다...'}</h3>
              <p style={{ color: 'var(--gray-500)', margin: 0 }}>잠시만 기다려주세요</p>
            </div>
          )}

          {/* 미리보기 단계 제거 */}

			{step === 6 && (
            <div style={{ display: 'grid', gap: 'var(--spacing-lg)' }}>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 44 }}>
                <button className="btn-secondary" onClick={handleBackNavigation}>이전</button>
              </div>
              <div>
                <h2 className="section-title mobile-section-title" style={{ margin: 0 }}>
                  {generationMode === 'manual' ? '퀴즈 직접 제작' : '퀴즈 저장'}
                </h2>
                {generationMode !== 'manual' && (
                  <p className="section-description" style={{ margin: 0, fontSize: isMobile ? '14px' : '16px' }}>{getMetaText()}</p>
                )}
              </div>
              {/* 정보 입력 UI와 동일한 그림자 카드 컨테이너 */}
              <div className="mobile-form-card" style={{
                background: '#fff',
                border: '1px solid var(--gray-200)',
                borderRadius: '16px',
                padding: 'var(--spacing-xl)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                display: 'grid',
                gap: 'var(--spacing-xl)'
              }}>
                <div style={{ 
                  display: 'grid', 
                  gap: 'var(--spacing-sm)',
                  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                  border: '2px solid var(--primary)',
                  borderRadius: '16px',
                  padding: 'var(--spacing-lg)',
                  position: 'relative',
                  boxShadow: '0 4px 20px rgba(0, 102, 255, 0.15)'
                }}>
                  {/* 강조 아이콘 */}
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '20px',
                    background: 'var(--primary)',
                    color: 'white',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    ✏️
                  </div>
                  
                  <label htmlFor="quizTitle" className="mobile-title" style={{ 
                    margin: 0, 
                    textAlign: 'left',
                    fontSize: '24px',
                    fontWeight: 800,
                    color: 'var(--primary)',
                    lineHeight: '1.3',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                  }}>📝 퀴즈 제목을 입력해주세요 *</label>
                  
                  <div style={{ position: 'relative' }}>
                    <input
                      id="quizTitle"
                      type="text"
                      className="mobile-input"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="예: 5학년 과학 상식 퀴즈 1"
                      style={{
                        width: '100%',
                        border: title.trim() ? '2px solid #10b981' : '2px solid var(--primary)',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        fontSize: '18px',
                        fontWeight: 600,
                        background: 'white',
                        boxShadow: title.trim() 
                          ? '0 0 0 4px rgba(16, 185, 129, 0.1)' 
                          : '0 0 0 4px rgba(0, 102, 255, 0.1)',
                        transition: 'all 0.3s ease',
                        color: 'var(--gray-900)'
                      }}
                      onFocus={(e) => {
                        e.target.style.boxShadow = '0 0 0 4px rgba(0, 102, 255, 0.2)';
                        e.target.style.borderColor = 'var(--primary)';
                      }}
                      onBlur={(e) => {
                        e.target.style.boxShadow = title.trim() 
                          ? '0 0 0 4px rgba(16, 185, 129, 0.1)' 
                          : '0 0 0 4px rgba(0, 102, 255, 0.1)';
                        e.target.style.borderColor = title.trim() ? '#10b981' : 'var(--primary)';
                      }}
                    />
                    {/* 입력 상태 표시 */}
                    {title.trim() && (
                      <div style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#10b981',
                        fontSize: '20px'
                      }}>
                        ✓
                      </div>
                    )}
                  </div>
                  
                  {/* 도움말 텍스트 */}
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--gray-600)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '4px'
                  }}>
                    <span style={{ 
                      background: 'var(--primary)', 
                      color: 'white', 
                      borderRadius: '50%', 
                      width: '16px', 
                      height: '16px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}>!</span>
                    제목은 퀴즈를 구분하는 중요한 정보입니다. 명확하게 작성해주세요.
                  </div>
                </div>
                {fakeQuiz && Array.isArray(fakeQuiz.questions) && fakeQuiz.questions.length > 0 && (
                  <div style={{ display: 'grid', gap: 'var(--spacing-md)' }}>
                    {/* 미리보기/편집 헤더 */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <h3 style={{ 
                        margin: 0,
                        fontSize: '20px',
                        fontWeight: 700,
                        color: 'var(--gray-900)',
                        lineHeight: '1.3'
                      }}>{isEditing ? '문제 편집' : '퀴즈 미리보기'}</h3>
                      <button
                        onClick={() => {
                          if (isEditing) {
                            // 편집 완료 시 검증 수행
                            if (!validateQuestions()) {
                              alert('빈칸을 모두 채워주세요.');
                              return;
                            }
                          }
                          setIsEditing((v) => !v);
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '8px 12px', borderRadius: 'var(--radius-md)',
                          background: isEditing ? 'rgba(16,185,129,0.10)' : 'var(--gray-50)',
                          border: isEditing ? '1px solid #10b981' : '1px solid var(--gray-300)',
                          color: isEditing ? '#065f46' : 'var(--gray-700)',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (isEditing) {
                            e.currentTarget.style.background = 'rgba(16,185,129,0.18)';
                            e.currentTarget.style.borderColor = '#059669';
                          } else {
                            e.currentTarget.style.background = 'var(--primary-light)';
                            e.currentTarget.style.borderColor = 'var(--primary)';
                            e.currentTarget.style.color = 'var(--primary-dark)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (isEditing) {
                            e.currentTarget.style.background = 'rgba(16,185,129,0.10)';
                            e.currentTarget.style.borderColor = '#10b981';
                            e.currentTarget.style.color = '#065f46';
                          } else {
                            e.currentTarget.style.background = 'var(--gray-50)';
                            e.currentTarget.style.borderColor = 'var(--gray-300)';
                            e.currentTarget.style.color = 'var(--gray-700)';
                          }
                        }}
                      >
                        {isEditing ? <><Check size={16} /> 편집 완료</> : <><Edit size={16} /> 편집</>}
                      </button>
                    </div>
                    {/* 본문 */}
                    {!isEditing && (
                      <>
                        {fakeQuiz.questions.map((q, idx) => (
                          <div key={idx} style={{
                            border: '1px solid var(--gray-200)',
                            borderRadius: 'var(--radius-md)',
                            padding: 'var(--spacing-md)'
                          }}>
                            <div style={{ fontWeight: 700, marginBottom: 'var(--spacing-sm)' }}>
                              Q{idx + 1}. {q.question}
                            </div>
                            <ol style={{ margin: 0, paddingLeft: '1.2rem', display: 'grid', gap: 4 }}>
                              {q.options.map((opt, i) => (
                                <li key={i} style={{
                                  color: i === q.answerIndex ? 'var(--primary)' : 'inherit',
                                  fontWeight: i === q.answerIndex ? 600 : 400
                                }}>
                                  {opt}
                                  {i === q.answerIndex && (
                                    <span style={{ marginLeft: 6, color: 'var(--primary)' }}>(정답)</span>
                                  )}
                                </li>
                              ))}
                            </ol>
                          </div>
                        ))}
                      </>
                    )}
                    {isEditing && (
                      <div style={{ display: 'grid', gap: 'var(--spacing-md)' }}>
                        {/* 문제 개수 표시 */}
                        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                          <span style={{ fontSize: 14, color: 'var(--gray-600)', fontWeight: 600 }}>
                            총 {fakeQuiz.questions.length}개 문제 (최대 20개)
                          </span>
                        </div>
                        
                        {/* 2열 그리드로 문제 배치 */}
                        <div className="mobile-grid-2" style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(2, 1fr)', 
                          gap: 'var(--spacing-md)'
                        }}>
                          {fakeQuiz.questions.map((q, idx) => {
                            const opts = Array.isArray(q.options) ? q.options.slice(0, 4) : [];
                            while (opts.length < 4) opts.push(`선택지 ${String.fromCharCode(65 + opts.length)}`);
                            const hasQuestionError = validationErrors.has(`question_${idx}`);
                            
                            return (
                              <div key={idx} style={{ 
                                border: hasQuestionError ? '2px solid #ef4444' : '1px solid var(--gray-200)', 
                                borderRadius: 'var(--radius-md)', 
                                padding: 'var(--spacing-md)', 
                                background: '#fff',
                                boxShadow: hasQuestionError ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : '0 2px 4px rgba(0,0,0,0.06)',
                                minHeight: 'fit-content'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-sm)' }}>
                                  <div style={{ fontWeight: 700, fontSize: 14 }}>문제 {idx + 1}</div>
                                  {fakeQuiz.questions.length > 1 && (
                                    <button
                                      onClick={() => deleteQuestion(idx)}
                                      style={{
                                        display: 'flex', alignItems: 'center', gap: 4,
                                        padding: '4px 8px', borderRadius: 'var(--radius-sm)',
                                        background: 'rgba(239, 68, 68, 0.1)',
                                        border: '1px solid #ef4444',
                                        color: '#dc2626',
                                        cursor: 'pointer',
                                        fontSize: 11
                                      }}
                                    >
                                      <Trash2 size={10} /> 삭제
                                    </button>
                                  )}
                                </div>
                                <textarea
                                  value={q.question || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.length <= 50) {
                                      handleQuestionTextChange(idx, value);
                                      // 입력 시 에러 상태 초기화
                                      if (validationErrors.has(`question_${idx}`)) {
                                        const newErrors = new Set(validationErrors);
                                        newErrors.delete(`question_${idx}`);
                                        setValidationErrors(newErrors);
                                      }
                                    }
                                  }}
                                  placeholder="문제 내용을 입력하세요 (최대 50글자)"
                                  rows={2}
                                  style={{ 
                                    width: '100%', 
                                    border: hasQuestionError ? '1px solid #ef4444' : '1px solid var(--gray-300)', 
                                    borderRadius: 8, 
                                    padding: '8px 10px', 
                                    marginBottom: 12,
                                    backgroundColor: hasQuestionError ? '#fef2f2' : '#fff',
                                    resize: 'none',
                                    fontSize: 13,
                                    lineHeight: '1.4'
                                  }}
                                />
                                <div style={{ display: 'grid', gap: 6 }}>
                                  {opts.map((opt, i) => {
                                    const hasOptionError = validationErrors.has(`option_${idx}_${i}`);
                                    return (
                                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <input
                                          type="radio"
                                          name={`answer_${idx}`}
                                          checked={Number(q.answerIndex) === i}
                                          onChange={() => handleAnswerIndexChange(idx, i)}
                                          style={{ transform: 'scale(0.9)' }}
                                        />
                                        <input
                                          type="text"
                                          value={opt}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            if (value.length <= 15) {
                                              handleOptionTextChange(idx, i, value);
                                              // 입력 시 에러 상태 초기화
                                              if (validationErrors.has(`option_${idx}_${i}`)) {
                                                const newErrors = new Set(validationErrors);
                                                newErrors.delete(`option_${idx}_${i}`);
                                                setValidationErrors(newErrors);
                                              }
                                            }
                                          }}
                                          placeholder={`선택지 ${String.fromCharCode(65 + i)} (최대 15글자)`}
                                          style={{ 
                                            flex: 1, 
                                            border: hasOptionError ? '1px solid #ef4444' : '1px solid var(--gray-300)', 
                                            borderRadius: 6, 
                                            padding: '6px 8px',
                                            backgroundColor: hasOptionError ? '#fef2f2' : '#fff',
                                            fontSize: 12
                                          }}
                                        />
                                        {Number(q.answerIndex) === i && (
                                          <span style={{ fontSize: 10, color: 'var(--primary)', fontWeight: 600 }}>정답</span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* 문제 추가 버튼들 - 문제들 아래에 배치 */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          gap: 'var(--spacing-md)',
                          marginTop: 'var(--spacing-md)',
                          paddingTop: 'var(--spacing-md)',
                          borderTop: '1px dashed var(--gray-300)'
                        }}>
                          {/* AI로 퀴즈 추가 생성 버튼 - AI 모드일 때만 표시 */}
                          {generationMode === 'ai' && (
                            <button
                              onClick={generateMoreQuestions}
                              disabled={fakeQuiz.questions.length >= 20 || isGeneratingMore}
                              style={{
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                gap: 8,
                                minWidth: 180,
                                padding: '12px 20px', 
                                borderRadius: '12px',
                                background: (fakeQuiz.questions.length >= 20 || isGeneratingMore)
                                  ? 'var(--gray-100)' 
                                  : 'linear-gradient(90deg, #0066FF 0%, #4DB9FF 100%)',
                                border: (fakeQuiz.questions.length >= 20 || isGeneratingMore)
                                  ? '2px dashed var(--gray-300)' 
                                  : 'none',
                                color: (fakeQuiz.questions.length >= 20 || isGeneratingMore)
                                  ? 'var(--gray-400)' 
                                  : '#ffffff',
                                cursor: (fakeQuiz.questions.length >= 20 || isGeneratingMore)
                                  ? 'not-allowed' 
                                  : 'pointer',
                                transition: 'all 0.3s ease',
                                fontWeight: 600,
                                fontSize: 14,
                                boxShadow: (fakeQuiz.questions.length >= 20 || isGeneratingMore)
                                  ? 'none' 
                                  : '0 4px 12px rgba(0, 102, 255, 0.25)'
                              }}
                              onMouseEnter={(e) => {
                                if (fakeQuiz.questions.length < 20 && !isGeneratingMore) {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 102, 255, 0.35)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (fakeQuiz.questions.length < 20 && !isGeneratingMore) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 102, 255, 0.25)';
                                }
                              }}
                            >
                              {isGeneratingMore ? (
                                <>
                                  <Loader2 className="spin" size={18} />
                                  AI 생성 중...
                                </>
                              ) : (
                                <>
                                  <img
                                    src={`${process.env.PUBLIC_URL}/assets/images/contents/shortForm/AIQuizGenerationCharacter.png`}
                                    alt="AI"
                                    style={{ width: 18, height: 18, objectFit: 'contain' }}
                                  />
                                  {fakeQuiz.questions.length >= 20 ? '최대 문제 수 도달' : 'AI로 퀴즈 추가'}
                                </>
                              )}
                            </button>
                          )}
                          
                          {/* 새 문제 추가 버튼 */}
                          <button
                            onClick={addQuestion}
                            disabled={fakeQuiz.questions.length >= 20}
                            style={{
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              gap: 8,
                              minWidth: 160,
                              padding: '12px 20px', 
                              borderRadius: '12px',
                              background: fakeQuiz.questions.length >= 20 
                                ? 'var(--gray-100)' 
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              border: fakeQuiz.questions.length >= 20 
                                ? '2px dashed var(--gray-300)' 
                                : 'none',
                              color: fakeQuiz.questions.length >= 20 
                                ? 'var(--gray-400)' 
                                : '#ffffff',
                              cursor: fakeQuiz.questions.length >= 20 
                                ? 'not-allowed' 
                                : 'pointer',
                              transition: 'all 0.3s ease',
                              fontWeight: 600,
                              fontSize: 14,
                              boxShadow: fakeQuiz.questions.length >= 20 
                                ? 'none' 
                                : '0 4px 12px rgba(102, 126, 234, 0.25)'
                            }}
                            onMouseEnter={(e) => {
                              if (fakeQuiz.questions.length < 20) {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.35)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (fakeQuiz.questions.length < 20) {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.25)';
                              }
                            }}
                          >
                            <Plus size={18} />
                            {fakeQuiz.questions.length >= 20 ? '최대 문제 수 도달' : '새 문제 추가'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div style={{ 
                position: 'relative', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                minHeight: 44,
                paddingTop: '20px'
              }}>
                <button className="btn-primary mobile-button-large" onClick={handleSaveWithTitle} disabled={!title.trim() || isSaving}>
                  {isSaving ? '저장 중...' : '저장하기'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 나가기 확인 모달 */}
        {showExitModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="mobile-modal" style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: 'var(--spacing-xl)',
              maxWidth: '480px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              animation: 'modalFadeIn 0.2s ease-out'
            }}>
              <style>{`
                @keyframes modalFadeIn {
                  from { opacity: 0; transform: scale(0.95); }
                  to { opacity: 1; transform: scale(1); }
                }
              `}</style>
              
              <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-lg)' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#fee2e2',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto var(--spacing-md)'
                }}>
                  <ArrowLeft size={24} style={{ color: '#dc2626' }} />
                </div>
                
                <h3 style={{
                  margin: '0 0 var(--spacing-sm) 0',
                  fontSize: '20px',
                  fontWeight: 700,
                  color: 'var(--gray-900)'
                }}>
                  정말 나가시겠어요?
                </h3>
                
                <p style={{
                  margin: 0,
                  color: 'var(--gray-600)',
                  lineHeight: 1.5
                }}>
                  작업 중인 퀴즈 내용이 저장되지 않고<br />
                  모두 사라집니다.
                </p>
              </div>

              <div style={{
                display: 'flex',
                gap: 'var(--spacing-sm)',
                justifyContent: 'center'
              }}>
                <button
                  onClick={cancelExit}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    border: '1px solid var(--gray-300)',
                    borderRadius: '12px',
                    backgroundColor: 'white',
                    color: 'var(--gray-700)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--gray-50)';
                    e.currentTarget.style.borderColor = 'var(--gray-400)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = 'var(--gray-300)';
                  }}
                >
                  계속 작업하기
                </button>
                
                <button
                  onClick={confirmExit}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    border: 'none',
                    borderRadius: '12px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#b91c1c';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#dc2626';
                  }}
                >
                  나가기
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ShortFormQuizSettingPage;


